<template>
  <div
    ref="customSelectDatePickerRef"
    class="custom-select-datepicker"
    @keydown.capture="onComponentKeydown"
  >
    <CustomSelect
      ref="customSelectRef"
      v-if="!state.showDatepicker"
      v-model="state.selectedRange"
      @update:model-value="selectOption"
      :filterable="false"
      :searchable="false"
      :close-on-select="true"
      :options="dateRanges"
      :placeholder="placeholder"
      class="custom-select-datepicker-date-select"
      :append-icon="'ri-calendar-line'"
      :input-id="inputId || undefined"
      :accessible-label="accessibleLabel || placeholder"
    >
    </CustomSelect>

    <CustomDatePicker
      v-if="state.showDatepicker"
      :is-range="true"
      :min-date="calendarMinDate"
      :max-date="calendarMaxDate"
      :placeholder="'Select Date Range'"
      :is-custom-select="state.showDatepicker"
      v-model="state.selectedRangeCalendar"
      @update:model-value="onCloseCalendar"
    >
    </CustomDatePicker>
  </div>
</template>
<script>
import { reactive, computed, nextTick, ref, watch } from "vue"
import helpers from "@/helpers/index.js"
import moment from "moment-timezone"
import CustomSelect from "./CustomSelect.vue"
import CustomDatePicker from "./CustomDatePicker.vue"

export default {
  components: { CustomSelect, CustomDatePicker },
  emits: ["update:modelValue"],
  props: {
    dateRanges: Array,
    modelValue: [String, Object],
    placeholder: {
      type: String,
      default: "Select Date Range"
    },
    accessibleLabel: {
      type: String,
      default: ""
    },
    inputId: {
      type: String,
      default: ""
    },
    limitMaxDate: {
      type: Boolean,
      default: false
    },
    shouldPersistValue: {
      type: Boolean,
      default: true
    },
    selectedSchoolYearLimit: {
      type: Object,
      default: () => ({})
    },
    addCustomRangeInitialValue: {
      type: Boolean,
      default: false
    },
    useSelectedSchoolYearLimitEndDate: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const customSelectRef = ref(null)
    const customSelectDatePickerRef = ref(null)

    const state = reactive({
      showDatepicker: false,
      selectedRange: props?.dateRanges ? props?.dateRanges?.[0]?.range : "",
      selectedRangeCalendar: null,
      startDate: null,
      endDate: null,
      maxSchoolYearDate: null,
      minSchoolYearDate: null
    })

    const calendarMinDate = computed(() => {
      return props?.selectedSchoolYearLimit?.value
        ? new Date(props?.selectedSchoolYearLimit?.value[0], 6, 1)
        : helpers.minSchoolYearDate()
    })

    const calendarMaxDate = computed(() => {
      if (props.limitMaxDate) {
        if (
          props.useSelectedSchoolYearLimitEndDate &&
          props?.selectedSchoolYearLimit?.endDate
        ) {
          const endDate = moment(
            props?.selectedSchoolYearLimit?.endDate,
            "MM/DD/YYYY"
          )
          return new Date(helpers.currTzDate(endDate))
        }
        const today = moment()
        return new Date(helpers.currTzDate(today))
      } else {
        return props?.selectedSchoolYearLimit?.value
          ? new Date(props?.selectedSchoolYearLimit?.value[1], 5, 30)
          : helpers.maxSchoolYearDate()
      }
    })

    const minDate = computed(() => {
      return helpers.minSchoolYearDate()
    })

    const maxDate = computed(() => {
      const today = moment()
      return props.limitMaxDate
        ? new Date(helpers.currTzDate(today))
        : helpers.maxSchoolYearDate()
    })

    const selectableDateRanges = computed(() => {
      return Array.isArray(props?.dateRanges)
        ? props.dateRanges.filter((range) => !!range)
        : []
    })

    const getRangeFromItem = (item) => {
      if (!item || typeof item !== "object") return ""
      return item?.range || ""
    }

    const getCurrentRangeIndex = () => {
      const ranges = selectableDateRanges.value
      if (!ranges.length) return -1

      const selectedRangeValue =
        typeof state.selectedRange === "string"
          ? state.selectedRange
          : getRangeFromItem(state.selectedRange)

      return ranges.findIndex(
        (item) => getRangeFromItem(item) === selectedRangeValue
      )
    }

    const focusMainSelect = () => {
      nextTick(() => {
        const selectRoot = customSelectRef?.value?.$el || null
        const selectTrigger =
          selectRoot?.querySelector?.('[role="combobox"]') ||
          selectRoot?.querySelector?.(".vs__search") ||
          null
        selectTrigger?.focus?.()
      })
    }

    const selectRangeByIndex = (index) => {
      const ranges = selectableDateRanges.value
      if (!ranges.length || index < 0 || index >= ranges.length) return

      state.selectedRange = ranges[index]
      selectOption()
    }

    watch(
      () => props.modelValue,
      (modelValueRange) => {
        state.showDatepicker = false
        state.selectedRange = props?.dateRanges?.[0]?.range || ""
        if (modelValueRange) {
          state.selectedRange = modelValueRange.range
            ? modelValueRange.range
            : modelValueRange
          if (modelValueRange.id === "custom") {
            state.selectedRangeCalendar = {
              start: modelValueRange.startDate
                ? modelValueRange.startDate
                : null,
              end: modelValueRange.endDate ? modelValueRange.endDate : null
            }
            if (
              modelValueRange.current_school_year &&
              modelValueRange.current_school_year != modelValueRange.range
            ) {
              state.showDatepicker = true
            } else {
              state.showDatepicker = false
            }
            state.minSchoolYearDate = modelValueRange.selected_school_year
              ? modelValueRange.selected_school_year.split(" - ")[0]
              : null
            state.maxSchoolYearDate = modelValueRange.selected_school_year
              ? modelValueRange.selected_school_year.split(" - ")[1]
              : null
            state.startDate = modelValueRange.startDate
              ? modelValueRange.startDate
              : null
            state.endDate = modelValueRange.endDate
              ? modelValueRange.endDate
              : null
          }
        }
      }
    )

    const selectOption = () => {
      const selectedRangeOption = state.selectedRange || {}
      if (selectedRangeOption?.id === "custom") {
        state.startDate = selectedRangeOption.startDate
          ? selectedRangeOption.startDate
          : null
        state.endDate = selectedRangeOption.endDate
          ? selectedRangeOption.endDate
          : null
        state.showDatepicker = true

        const customRange = selectedRangeOption.range
        if (
          props.addCustomRangeInitialValue &&
          customRange &&
          customRange.includes(" - ")
        ) {
          const [startDateStr, endDateStr] = customRange.split(" - ")
          state.selectedRangeCalendar = {
            start: moment(startDateStr, "MM/DD/YYYY")._d,
            end: moment(endDateStr, "MM/DD/YYYY")._d
          }
        } else if (
          props.shouldPersistValue &&
          props.modelValue &&
          props.modelValue.includes(" - ")
        ) {
          state.selectedRangeCalendar = {
            start: moment(props.modelValue.split(" - ")[0], "MM/DD/YYYY")._d,
            end: moment(props.modelValue.split(" - ")[1], "MM/DD/YYYY")._d
          }
        } else {
          state.selectedRangeCalendar = {
            start: moment()._d,
            end: moment()._d
          }
        }
      } else {
        setRange(false)
        focusMainSelect()
      }
    }

    const setRange = (isFromCalendar) => {
      let value

      if (isFromCalendar) {
        const range =
          helpers.currTzDate(state.selectedRangeCalendar.start) +
          " - " +
          helpers.currTzDate(state.selectedRangeCalendar.end)

        value = range
        state.selectedRange = range
        if (
          moment(helpers.currTzDate(state.selectedRangeCalendar.start)).isAfter(
            helpers.currTzDate(helpers.minSchoolYearDate())
          ) &&
          moment(helpers.currTzDate(state.selectedRangeCalendar.end)).isBefore(
            helpers.currTzDate(helpers.maxSchoolYearDate())
          )
        ) {
          state.showDatepicker = false
        } else {
          state.showDatepicker = true
        }
      } else {
        const selectedRangeOption = state.selectedRange || {}
        if (selectedRangeOption?.id !== "custom") {
          value = selectedRangeOption.range
        } else {
          value = state.selectedRange
        }
        state.selectedRange = selectedRangeOption.range
      }

      emit("update:modelValue", value)
    }
    const onCloseCalendar = () => {
      setRange(true)
      focusMainSelect()
    }

    const closeDatepickerAndFocusMainSelect = () => {
      if (!state.showDatepicker) return
      state.showDatepicker = false
      focusMainSelect()
    }

    const onComponentKeydown = (event) => {
      const key = event?.key
      if (!key) return

      if (key === "Escape") {
        event.preventDefault()
        closeDatepickerAndFocusMainSelect()
        return
      }

      if (state.showDatepicker) return

      const navigationKeys = [
        "ArrowDown",
        "ArrowUp",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End"
      ]
      if (!navigationKeys.includes(key)) return

      const rootElement = customSelectDatePickerRef?.value
      const eventTarget = event?.target
      if (!rootElement || !eventTarget || !rootElement.contains(eventTarget))
        return

      const isComboboxTarget = !!eventTarget?.closest?.('[role="combobox"]')
      if (!isComboboxTarget) return

      const ranges = selectableDateRanges.value
      if (!ranges.length) return

      event.preventDefault()
      const lastIndex = ranges.length - 1
      const currentIndex = getCurrentRangeIndex()
      const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0

      let nextIndex = safeCurrentIndex
      if (key === "ArrowDown" || key === "ArrowRight") {
        nextIndex = safeCurrentIndex >= lastIndex ? 0 : safeCurrentIndex + 1
      } else if (key === "ArrowUp" || key === "ArrowLeft") {
        nextIndex = safeCurrentIndex <= 0 ? lastIndex : safeCurrentIndex - 1
      } else if (key === "Home") {
        nextIndex = 0
      } else if (key === "End") {
        nextIndex = lastIndex
      }

      selectRangeByIndex(nextIndex)
      focusMainSelect()
    }

    return {
      customSelectRef,
      customSelectDatePickerRef,
      state,
      minDate,
      maxDate,
      setRange,
      onCloseCalendar,
      selectOption,
      calendarMinDate,
      calendarMaxDate,
      onComponentKeydown
    }
  }
}
</script>
